import { useState } from 'react';
import { insertChantierDocuments } from '../lib/chantierService';
import { removeStoredDocument, uploadChantierExtractionDocument } from '../lib/fileService';
import { triggerLotUpdateExtraction } from '../lib/lotExtractionService';
import LotsReviewStep from './LotsReviewStep';
import MultiDocCard from './MultiDocCard';
import './NewChantierModal.css';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const DOC_SECTIONS = [
  {
    type: 'marche',
    title: 'Marché de travaux',
    subtitle: 'CCAP, CCTP, DPGF, avenant ou décomposition des lots',
    hint: 'PDF ou Excel — max 50 Mo par fichier',
    accept: '.pdf,.xls,.xlsx',
    addLabel: 'Importer un ou plusieurs documents',
    addLabelMore: "Ajouter d'autres documents",
  },
  {
    type: 'planning',
    title: 'Planning prévisionnel',
    subtitle: 'Planning général ou Gantt par lot',
    hint: 'PDF ou Excel — max 50 Mo par fichier',
    accept: '.pdf,.xls,.xlsx',
    addLabel: 'Importer un planning',
    addLabelMore: 'Ajouter un autre planning',
  },
  {
    type: 'complementaire',
    title: 'Documents complémentaires',
    subtitle: 'Devis, contrats, notes... un ou plusieurs PDF',
    hint: 'PDF uniquement — max 50 Mo par fichier',
    accept: '.pdf',
    addLabel: 'Importer un ou plusieurs PDF',
    addLabelMore: "Ajouter d'autres PDF",
  },
];

function ChantierUpdateModal({ chantier, userId, onClose, onUpdated }) {
  const [docsByType, setDocsByType] = useState({ marche: [], planning: [], complementaire: [] });
  const [uploadingType, setUploadingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reviewDocumentIds, setReviewDocumentIds] = useState(null);

  const allDocs = Object.values(docsByType).flat();
  const canSubmit = allDocs.length > 0 && uploadingType === null && !submitting;

  async function pickDocuments(type, fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
    setError(
      validFiles.length < files.length
        ? `${files.length - validFiles.length} fichier(s) dépassent 50 Mo et n'ont pas été ajoutés.`
        : null
    );
    if (validFiles.length === 0) return;

    setUploadingType(type);
    try {
      const uploaded = await Promise.all(
        validFiles.map((file) => uploadChantierExtractionDocument(file, chantier.id, type))
      );
      setDocsByType((prev) => ({ ...prev, [type]: [...prev[type], ...uploaded] }));
    } catch (err) {
      setError(`Import impossible : ${err.message}`);
    } finally {
      setUploadingType(null);
    }
  }

  function removeDocument(type, index) {
    const removed = docsByType[type][index];
    setDocsByType((prev) => ({ ...prev, [type]: prev[type].filter((_, i) => i !== index) }));
    if (removed?.storage_path) {
      removeStoredDocument(removed.storage_path).catch(() => {});
    }
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const insertedDocuments = await insertChantierDocuments(chantier.id, userId, allDocs);
      const documentIds = insertedDocuments.map((d) => d.id);
      await triggerLotUpdateExtraction(chantier.id, documentIds);
      setReviewDocumentIds(documentIds);
    } catch (err) {
      setError(`Impossible de lancer l'analyse : ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (reviewDocumentIds) {
    return (
      <div className="ModalOverlay">
        <div className="Modal LotsReviewModal" onClick={(e) => e.stopPropagation()}>
          <LotsReviewStep
            chantierId={chantier.id}
            documentIds={reviewDocumentIds}
            onFinish={onUpdated}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="ModalOverlay" onClick={submitting ? undefined : onClose}>
      <div className="Modal" onClick={(e) => e.stopPropagation()}>
        <div className="Modal-header">
          <div>
            <h2 className="Modal-title">Mettre à jour le chantier</h2>
            <p className="Modal-subtitle">{chantier.name} — via l'automatisation</p>
          </div>
          <button className="Modal-close" onClick={onClose} disabled={submitting} aria-label="Fermer">
            ×
          </button>
        </div>

        <div className="Modal-body">
          {error && <p className="Modal-error">{error}</p>}

          <div className="Modal-form">
            {DOC_SECTIONS.map((section) => (
              <MultiDocCard
                key={section.type}
                title={section.title}
                subtitle={section.subtitle}
                hint={section.hint}
                accept={section.accept}
                addLabel={section.addLabel}
                addLabelMore={section.addLabelMore}
                docs={docsByType[section.type]}
                uploading={uploadingType === section.type}
                onPick={(files) => pickDocuments(section.type, files)}
                onRemove={(index) => removeDocument(section.type, index)}
              />
            ))}

            <p className="Modal-info">
              L'analyse compare ces documents aux lots existants : elle ajoute les nouveaux lots,
              complète les informations manquantes et ajoute les tâches décrites. Aucune donnée
              existante n'est écrasée, et vous pourrez tout vérifier avant de valider.
            </p>
          </div>
        </div>

        <div className="Modal-footer">
          <button className="Modal-btn Modal-btn-secondary" onClick={onClose} disabled={submitting}>
            Annuler
          </button>
          <button className="Modal-btn Modal-btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? (
              <span className="Modal-btn-loading">
                <span className="Modal-btnSpinner" />
                Envoi en cours…
              </span>
            ) : (
              "Lancer l'analyse"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChantierUpdateModal;
